/**
 * Service for handling outgoing passport operations
 * Centralizes logic for loading and restoring outgoing passport submission data
 */

import { Injectable, inject } from '@angular/core';
import { OfflineStorageService } from './offline-storage.service';
import { PassportDetailsStore, type PassportDetails } from './passport-details.store';
import { PassportService } from './passport.service';
import { UiStateService } from './ui-state.service';

type OutgoingPassportSubmission = { data: Partial<PassportDetails> };

function isOutgoingPassportSubmission(value: unknown): value is OutgoingPassportSubmission {
  return typeof value === 'object' && value !== null && 'data' in value;
}

@Injectable({
  providedIn: 'root'
})
export class OutgoingPassportService {
  private readonly offlineStorage = inject(OfflineStorageService);

  /**
   * Load outgoing passport submission data from IndexedDB
   */
  async getOutgoingPassportData(passportId: string): Promise<unknown> {
    try {
      return await this.offlineStorage.getOutgoingPassport(passportId);
    } catch (error) {
      console.warn(`Failed to load outgoing passport data for ${passportId}:`, error);
      return null;
    }
  }

  /**
   * Restore outgoing passport data to the details store
   * Ensures modified images (deleted/new) are restored correctly
   */
  restoreOutgoingPassportToStore(
    store: PassportDetailsStore,
    passportId: string,
    outgoingData: unknown
  ): void {
    if (!isOutgoingPassportSubmission(outgoingData)) {
      return;
    }

    const payload = outgoingData.data;
    if (!payload) {
      return;
    }

    console.log(`📤 Restoring outgoing passport ${passportId} for list/detail display`);
    
    store.update(passportId, {
      id: passportId,
      ...payload,
      // Ensure images are restored correctly
      haccpImages: payload.haccpImages ?? [],
      haccpImageFiles: payload.haccpImageFiles ?? [],
      deletedImageFilenames: payload.deletedImageFilenames ?? []
    });
  }

  /**
   * Load and restore outgoing passport data if it exists
   * Returns true if data was restored, false otherwise
   */
  async loadAndRestoreOutgoingPassport(
    store: PassportDetailsStore,
    passportId: string
  ): Promise<boolean> {
    const outgoingData = await this.getOutgoingPassportData(passportId);
    
    if (outgoingData && typeof outgoingData === 'object') {
      this.restoreOutgoingPassportToStore(store, passportId, outgoingData);
      return true;
    }
    
    return false;
  }

  /**
   * Merge outgoing passports with regular passport records.
   * When detailsStore is provided, list/detail display uses IndexedDB submission data.
   */
  async mergeOutgoingPassportsWithRecords(
    passportService: PassportService,
    uiService: UiStateService,
    detailsStore?: PassportDetailsStore,
  ): Promise<void> {
    try {
      const outgoingPassports = await this.offlineStorage.getAllOutgoingPassports();
      
      if (outgoingPassports.length === 0) {
        return;
      }

      console.log(`📤 Found ${outgoingPassports.length} outgoing passports in IndexedDB`);
      
      const currentRecords = passportService.records();
      const updatedRecords = [...currentRecords];
      
      for (const outgoing of outgoingPassports) {
        const existingIndex = updatedRecords.findIndex(r => r.id === outgoing.passportId);
        
        if (existingIndex >= 0) {
          updatedRecords[existingIndex] = {
            ...updatedRecords[existingIndex],
            status: 'outgoing',
            isSynced: false
          };
        } else {
          updatedRecords.push({
            id: outgoing.passportId,
            status: 'outgoing',
            isSynced: false
          });
        }

        if (detailsStore) {
          this.restoreOutgoingPassportToStore(detailsStore, outgoing.passportId, outgoing.data);
        }
      }
      
      passportService.records.set(updatedRecords);
      
      if (uiService.statusSelected() !== 'outgoing') {
        console.log(`🔄 Switching to outgoing tab (${outgoingPassports.length} outgoing passports)`);
        uiService.setStatusSelected('outgoing');
      }
    } catch (error) {
      console.warn('Failed to merge outgoing passports:', error);
    }
  }
}



